import React, { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../utils/dateUtils';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';
import AutomatedScanningConfig from '../components/AutomatedScanningConfig';
import {
  PlusIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  CogIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState(new Set());

  useEffect(() => {
    fetchDomains();

    // Set up an interval to refresh domain data every 10 seconds
    // This ensures the last scan date is updated on the page quickly after scans
    const refreshIntervalId = setInterval(() => {
      fetchDomains();
    }, 10000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(refreshIntervalId);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(fetchDomains, 60000); // 1 minute
    setRefreshInterval(intervalId);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchDomains();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchDomains = async () => {
    try {
      // Fetch with cache busting to ensure fresh data
      const response = await api.getDomains(); // Use cache-busting method
      setDomains(response.data);
      setError('');
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      setError('Failed to load domains');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setAddingDomain(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/domains', { domain_name: newDomain, verification_email: verificationEmail });

      setNewDomain('');
      setVerificationEmail('');
      setShowAddForm(false);
      fetchDomains();

      const warningMsg = response.data.warning_message;

      setSuccess(
        <div className="flex flex-col space-y-2">
          <p className="font-semibold text-base">Domain added successfully!</p>
          <ul className="list-disc list-inside space-y-1 opacity-90">
            <li>Please check your email to verify ownership.</li>
            <li>Once verified, the first scan will start automatically.</li>
          </ul>
          {warningMsg && (
            <p className="mt-1 font-medium text-yellow-600 dark:text-yellow-400">
              Warning: {warningMsg}
            </p>
          )}
        </div>
      );
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) {
      return;
    }

    try {
      await api.delete(`/domains/${domainId}`);
      fetchDomains();
      setSuccess('Domain deleted successfully');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete domain');
    }
  };

  const getStatusIcon = (isVerified) => {
    if (isVerified) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusText = (isVerified) => {
    if (isVerified) {
      return 'Verified';
    } else {
      return 'Unverified';
    }
  };

  const toggleDomainExpansion = (domainId) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <TGChibiVariants.Loading message="Loading your domains..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Domains</h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Manage your domains and their verification status
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                Add Domain
              </button>
            </div>
          </div>
        </div>

        {(error || success) && (
          <div className={`mb-4 ${error ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'} rounded-md p-4`}>
            <div className={`text-sm ${error ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>{error || success}</div>
          </div>
        )}

        {/* Add Domain Form */}
        {showAddForm && (
          <div className="mb-6 bg-white dark:bg-dark-800 shadow-glow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Add New Domain
            </h3>
            <form onSubmit={handleAddDomain} className="space-y-4">
              <div>
                <label htmlFor="domain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Domain Name
                </label>
                <input
                  type="text"
                  id="domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  className="mt-1 block w-full border-gray-300 dark:border-dark-600 rounded-md shadow-sm focus:ring-mint-500 focus:border-mint-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  required
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Enter the domain you want to monitor
                </p>
              </div>
              <div>
                <label htmlFor="verificationEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Verification Email
                </label>
                <input
                  type="email"
                  id="verificationEmail"
                  value={verificationEmail}
                  onChange={(e) => setVerificationEmail(e.target.value)}
                  placeholder={`admin@${newDomain || 'yourdomain.com'}`}
                  className="mt-1 block w-full border-gray-300 dark:border-dark-600 rounded-md shadow-sm focus:ring-mint-500 focus:border-mint-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  required
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Enter an email address on the domain you're verifying (e.g., admin@{newDomain || 'yourdomain.com'})
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={addingDomain || !newDomain || !verificationEmail}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 disabled:opacity-50"
                >
                  {addingDomain ? <LoadingSpinner size="sm" /> : 'Add Domain'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewDomain('');
                    setVerificationEmail('');
                    setError('');
                    setSuccess('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Domain Verification Warning */}
        <div className="mb-6 rounded-md bg-green-50 dark:bg-green-900/10 p-4 border border-green-500 dark:border-green-500 shadow-glow">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800 dark:text-green-300">
                Domain Verification Required
              </h3>
              <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                <p>
                  To verify domain ownership, provide an email address ending in the domain name (e.g., name@yourdomain.com).
                  We'll send a verification email to that address.
                </p>
                <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800">
                  <p className="font-semibold text-xs uppercase tracking-wide opacity-75 mb-1">
                    No email access?
                  </p>
                  <p>
                    If you cannot create an email for this domain (e.g., it's a reserved/parked domain), please use the <a href="/contact" className="underline hover:text-green-900 dark:hover:text-green-200">contact form</a> to explain verification. An admin can manually enable the domain for you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Domains List */}
        <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg overflow-hidden border border-gray-200 dark:border-dark-600">
          {domains.length === 0 ? (
            <div className="text-center py-12">
              <TGChibiVariants.Empty />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No domains</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by adding your first domain to monitor.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Add Domain
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
                  <thead className="bg-gray-50 dark:bg-dark-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Domain
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Added
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Last Scan
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
                    {domains.map((domain) => (
                      <React.Fragment key={domain.id}>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() => toggleDomainExpansion(domain.id)}
                                className="mr-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-600"
                              >
                                {expandedDomains.has(domain.id) ? (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                )}
                              </button>
                              <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {domain.domain_name}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(domain.is_verified)}
                              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                                {getStatusText(domain.is_verified)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(domain.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {domain.last_scan_at ? formatDate(domain.last_scan_at) : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => toggleDomainExpansion(domain.id)}
                                disabled={!domain.is_verified}
                                className={`${!domain.is_verified
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300'
                                  }`}
                                title={
                                  !domain.is_verified
                                    ? "Verification required to scan. Please check your email."
                                    : (domain.automated_scanning_enabled ? "Configure (Automated scanning enabled)" : "Configure")
                                }
                              >
                                {domain.automated_scanning_enabled ? (
                                  <div className="flex items-center">
                                    <CogIcon className="h-5 w-5" />
                                    <ClockIcon className="h-4 w-4 ml-1 text-green-500" />
                                  </div>
                                ) : (
                                  <CogIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteDomain(domain.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedDomains.has(domain.id) && (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 bg-gray-50 dark:bg-dark-700">
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                                  <CogIcon className="h-4 w-4 mr-2" />
                                  Domain Configuration
                                </h4>
                                <AutomatedScanningConfig
                                  domain={domain}
                                  onUpdate={fetchDomains}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {domains.map((domain) => (
                  <div key={domain.id} className="bg-white dark:bg-dark-800 rounded-lg shadow-glow border border-gray-200 dark:border-dark-600 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {domain.domain_name}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleDomainExpansion(domain.id)}
                          disabled={!domain.is_verified}
                          className={`${!domain.is_verified
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300'
                            } p-1`}
                          title={
                            !domain.is_verified
                              ? "Verification required to scan. Please check your email."
                              : (domain.automated_scanning_enabled ? "Configure (Automated scanning enabled)" : "Configure")
                          }
                        >
                          {domain.automated_scanning_enabled ? (
                            <div className="flex items-center">
                              <CogIcon className="h-5 w-5" />
                              <ClockIcon className="h-4 w-4 ml-1 text-green-500" />
                            </div>
                          ) : (
                            <CogIcon className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteDomain(domain.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-3 text-sm">
                      <div className="flex items-center">
                        <div className="w-24 text-gray-500 dark:text-gray-400">Status:</div>
                        <div className="flex items-center">
                          {getStatusIcon(domain.is_verified)}
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {getStatusText(domain.is_verified)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 text-gray-500 dark:text-gray-400">Added:</div>
                        <div className="text-gray-900 dark:text-white">
                          {formatDate(domain.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 text-gray-500 dark:text-gray-400">Last Scan:</div>
                        <div className="text-gray-900 dark:text-white">
                          {domain.last_scan_at ? formatDate(domain.last_scan_at) : 'Never'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => toggleDomainExpansion(domain.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center"
                      >
                        {expandedDomains.has(domain.id) ? (
                          <>
                            <ChevronUpIcon className="h-4 w-4 mr-1" />
                            Hide Configuration
                          </>
                        ) : (
                          <>
                            <ChevronDownIcon className="h-4 w-4 mr-1" />
                            Show Configuration
                          </>
                        )}
                      </button>
                    </div>

                    {expandedDomains.has(domain.id) && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-600">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center mb-3">
                          <CogIcon className="h-4 w-4 mr-2" />
                          Domain Configuration
                        </h4>
                        <AutomatedScanningConfig
                          domain={domain}
                          onUpdate={fetchDomains}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Verification Help */}

      </div>
    </div>
  );
}
